import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { DateTimePicker } from "@mui/x-date-pickers"
import { isValid as isDateValid } from "date-fns"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { useIsMobile } from "../../../common/utils"
import { useDialogContainer } from "../utils"
import DialogHeader from "./DialogHeader"

const REASON_MAX_LENGTH = 500
const REASON_MIN_LENGTH = 3

export default function RescheduleBookingDialog({
  open,
  onClose,
  candidate,
  currentDatetime,
  onRescheduled,
}) {
  const [newDatetime, setNewDatetime] = useState(new Date())
  const [reason, setReason] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [acuityWarning, setAcuityWarning] = useState(null)
  const axios = useAuthorizedAxiosClient()
  const isMobile = useIsMobile()
  const Container = useDialogContainer()

  const trimmedReason = reason.trim()
  const reasonTooShort =
    trimmedReason.length > 0 && trimmedReason.length < REASON_MIN_LENGTH
  const isPastAppointment =
    isDateValid(newDatetime) && newDatetime.getTime() < Date.now()
  const canSubmit =
    isDateValid(newDatetime) &&
    trimmedReason.length >= REASON_MIN_LENGTH &&
    !isSubmitting

  function handleSoftClose() {
    onClose()
  }

  function handleSubmit() {
    setIsSubmitting(true)
    setErrorMessage(null)
    setAcuityWarning(null)
    axios
      .post(`/crm/api/candidates/${candidate.id}/reschedule-booking/`, {
        new_datetime: newDatetime.toISOString(),
        reason: trimmedReason,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      .then(({ data }) => {
        if (data.acuity_warning) {
          setAcuityWarning(data.acuity_warning)
        }
        onRescheduled(data)
        onClose(data)
      })
      .catch(({ response }) => {
        const detail = response?.data?.detail
        if (typeof detail === "string") {
          setErrorMessage(detail)
        } else if (response?.data?.reason) {
          setErrorMessage(response.data.reason[0])
        } else {
          setErrorMessage("Could not reschedule this appointment.")
        }
      })
      .finally(() => setIsSubmitting(false))
  }

  useEffect(() => {
    if (!open) {
      return
    }
    setReason("")
    setErrorMessage(null)
    setAcuityWarning(null)
    if (currentDatetime) {
      setNewDatetime(new Date(currentDatetime))
    } else if (candidate?.stamp) {
      setNewDatetime(new Date(candidate.stamp))
    } else {
      setNewDatetime(new Date())
    }
  }, [open, currentDatetime, candidate?.stamp])

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
    >
      <DialogHeader title="Reschedule Appointment" onClose={handleSoftClose} />
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Update the appointment time for {candidate?.candidate_name}. The
            reason is saved on the candidate timeline so your team can see why
            the meeting moved.
          </Typography>
          {candidate?.acuity_appointment_id ? (
            <Alert severity="info">
              This booking is linked to Acuity. We will attempt to update Acuity
              automatically when you save.
            </Alert>
          ) : null}
          {isPastAppointment ? (
            <Alert severity="warning">
              The selected time is in the past. You can still save if this is a
              correction.
            </Alert>
          ) : null}
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {acuityWarning ? (
            <Alert severity="warning">{acuityWarning}</Alert>
          ) : null}
          <DateTimePicker
            label="New appointment date & time"
            value={newDatetime}
            onChange={setNewDatetime}
            slotProps={{
              textField: { fullWidth: true, required: true },
            }}
          />
          <TextField
            label="Reason for reschedule"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            required
            inputProps={{ maxLength: REASON_MAX_LENGTH }}
            helperText={
              reasonTooShort
                ? `Reason must be at least ${REASON_MIN_LENGTH} characters.`
                : `${trimmedReason.length}/${REASON_MAX_LENGTH}`
            }
            error={reasonTooShort}
          />
        </Column>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleSoftClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          disabled={!canSubmit}
          startIcon={<EventRepeatOutlinedIcon />}
          onClick={handleSubmit}
        >
          Save new time
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}
